import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowLeft, CalendarClock, CalendarX2, CheckCircle2, Clock, Globe, MapPin, User, Video } from 'lucide-react';
import { apiPublica, errorDe } from './apiPublica';
import MarcoPublico, { Aviso, Cargando } from './MarcoPublico';
import SelectorHuecos from './SelectorHuecos';
import {
  capital, esUrl, fmtHora, nombreZona, textoDiaLargo, ymdDe, zonaNavegador,
} from '../../lib/calendario';

/** Gestionar una cita reservada (`/cita/:token`, enlace del correo de confirmación): ver, reprogramar, cancelar. */
export default function Cita() {
  const { token } = useParams();
  const [cita, setCita] = useState(null);
  const [error, setError] = useState('');
  const [zona, setZona] = useState(zonaNavegador());
  const [modo, setModo] = useState('ver'); // ver | reprogramar | cancelar
  const [pagina, setPagina] = useState(null);
  const [hueco, setHueco] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [aviso, setAviso] = useState('');
  const [recarga, setRecarga] = useState(0);
  const [hecho, setHecho] = useState('');

  const cargar = useCallback(() => apiPublica.get(`/cita/${encodeURIComponent(token)}`)
    .then(({ data }) => setCita(data))
    .catch((e) => setError(errorDe(e, 'Esta cita no existe'))), [token]);
  useEffect(() => { cargar(); }, [cargar]);

  const empezarReprogramar = async () => {
    setAviso('');
    try {
      const { data } = await apiPublica.get(`/reservar/${encodeURIComponent(cita.pagina.slug)}`);
      setPagina(data);
      setHueco(null);
      setModo('reprogramar');
    } catch (e) {
      setAviso(errorDe(e, 'Ahora mismo no se puede cambiar la hora de esta cita'));
    }
  };

  const reprogramar = async () => {
    setOcupado(true);
    setAviso('');
    try {
      await apiPublica.post(`/cita/${encodeURIComponent(token)}/reprogramar`, { inicio: hueco });
      await cargar();
      setModo('ver');
      setHecho('Tu cita cambió de hora. Te enviamos la confirmación por correo.');
    } catch (e) {
      if (e?.response?.status === 409) {
        setAviso(errorDe(e, 'Esa hora ya no está libre. Elige otra, por favor.'));
        setHueco(null);
        setRecarga((r) => r + 1);
      } else setAviso(errorDe(e, 'No se pudo cambiar la cita'));
    } finally {
      setOcupado(false);
    }
  };

  const cancelar = async () => {
    setOcupado(true);
    setAviso('');
    try {
      await apiPublica.post(`/cita/${encodeURIComponent(token)}/cancelar`, { motivo: motivo.trim() || undefined });
      await cargar();
      setModo('ver');
      setHecho('Tu cita se canceló. Te enviamos un correo con la cancelación.');
    } catch (e) {
      setAviso(errorDe(e, 'No se pudo cancelar la cita'));
    } finally {
      setOcupado(false);
    }
  };

  if (error) return <MarcoPublico titulo="Tu cita"><Aviso icono={<CalendarX2 className="pub-aviso-ico" />} titulo="No encontramos la cita" texto={error} /></MarcoPublico>;
  if (!cita) return <MarcoPublico titulo="Tu cita"><Cargando /></MarcoPublico>;

  const p = cita.pagina || {};
  const cancelada = cita.estado === 'cancelada';
  const cuando = `${capital(textoDiaLargo(ymdDe(cita.inicio, zona)))}, ${fmtHora(cita.inicio, zona)} – ${fmtHora(cita.fin, zona)}`;

  return (
    <MarcoPublico titulo={p.titulo || 'Tu cita'} ancho={modo === 'reprogramar' ? 'ancho' : 'normal'} color={p.color}>
      <div className="pub-cita" data-testid="pub-cita">
        <p className="pub-etiqueta" style={{ color: cancelada ? 'var(--danger)' : undefined }}>
          {cancelada ? 'Cita cancelada' : new Date(cita.fin) < new Date() ? 'Cita pasada' : 'Cita confirmada'}
        </p>
        <h1 className="pub-titulo" style={{ textDecoration: cancelada ? 'line-through' : undefined }}>{p.titulo}</h1>
        <p className="pub-dato pub-dato--fuerte"><CalendarClock />{cuando}</p>
        <p className="pub-dato"><Globe />{nombreZona(zona)}</p>
        {cita.anfitrion?.nombre && <p className="pub-dato"><User />Con {cita.anfitrion.nombre}</p>}
        {p.duracion && <p className="pub-dato"><Clock />{p.duracion} min</p>}
        {p.ubicacion && <p className="pub-dato"><MapPin />{esUrl(p.ubicacion) ? <a href={p.ubicacion} target="_blank" rel="noopener noreferrer nofollow">{p.ubicacion}</a> : p.ubicacion}</p>}
        {cita.meet_url && (
          <p className="pub-dato" data-testid="pub-cita-meet">
            <Video />
            <a className="pub-boton" href={cita.meet_url} target="_blank" rel="noopener noreferrer">Unirse con Orvian Meet</a>
          </p>
        )}
        <p className="pub-ayuda">A nombre de {cita.nombre} ({cita.correo})</p>

        {hecho && <p className="pub-exito" role="status"><CheckCircle2 className="h-4 w-4" />{hecho}</p>}
        {aviso && <p className="pub-error" role="alert">{aviso}</p>}

        {modo === 'ver' && cita.se_puede_cambiar && (
          <div className="pub-acciones">
            {p.activa && <button type="button" className="pub-boton" onClick={empezarReprogramar} data-testid="pub-reprogramar">Reprogramar</button>}
            <button type="button" className="pub-boton pub-boton--borde" onClick={() => { setAviso(''); setModo('cancelar'); }} data-testid="pub-cancelar">Cancelar la cita</button>
          </div>
        )}
        {modo === 'ver' && cancelada && p.slug && p.activa && (
          <div className="pub-acciones"><a className="pub-boton pub-boton--borde" href={`/reservar/${p.slug}`}>Reservar otra hora</a></div>
        )}

        {modo === 'cancelar' && (
          <div className="pub-form">
            <label className="pub-campo">Motivo (opcional)
              <textarea rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value)} maxLength={1000} placeholder="Se lo diremos a quien te atiende" data-testid="pub-motivo" />
            </label>
            <div className="pub-acciones">
              <button type="button" className="pub-boton pub-boton--peligro" disabled={ocupado} onClick={cancelar} data-testid="pub-confirmar-cancelar">{ocupado ? 'Cancelando…' : 'Sí, cancelar la cita'}</button>
              <button type="button" className="pub-boton pub-boton--borde" onClick={() => setModo('ver')}>Volver</button>
            </div>
          </div>
        )}

        {modo === 'reprogramar' && pagina && (
          <div className="pub-reprogramar">
            <button type="button" className="pub-volver" onClick={() => setModo('ver')}><ArrowLeft className="h-4 w-4" /> Volver</button>
            <h2 className="pub-subtitulo">Elige la nueva hora</h2>
            <SelectorHuecos slug={p.slug} ventanaDias={pagina.ventana_dias} zona={zona} onZona={setZona} elegido={hueco} onElegir={setHueco} recarga={recarga} color={p.color} />
            {hueco && (
              <div className="pub-confirmar-cambio">
                <span>Nueva hora: <b>{capital(textoDiaLargo(ymdDe(hueco, zona)))}, {fmtHora(hueco, zona)}</b></span>
                <button type="button" className="pub-boton" disabled={ocupado} onClick={reprogramar} data-testid="pub-confirmar-reprogramar">{ocupado ? 'Cambiando…' : 'Confirmar el cambio'}</button>
              </div>
            )}
          </div>
        )}
      </div>
    </MarcoPublico>
  );
}
